import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import ProductImage from '../components/ProductImage';
import SEO from '../components/SEO';
import useFeatureFlags from '../hooks/useFeatureFlags';
import './Cart.css';

const FRESH_MS = 7 * 24 * 60 * 60 * 1000;

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart, priceFor, resolvedPrices } = useCart();
  const navigate = useNavigate();
  const flags = useFeatureFlags();

  // Sprint R2 — total saved vs market across the cart. Only counts lines with a
  // fresh market price strictly above what the shopper pays.
  const marketSavings = cart.reduce((sum, item) => {
    const m = Number(item.market_price);
    const p = Number(priceFor(item));
    if (!Number.isFinite(m) || !Number.isFinite(p) || m <= p) return sum;
    if (item.market_price_at) {
      const ts = new Date(item.market_price_at).getTime();
      if (Number.isFinite(ts) && Date.now() - ts > FRESH_MS) return sum;
    }
    return sum + (m - p) * item.quantity;
  }, 0);
  const showSavings = flags.market_compare_enabled !== false && marketSavings >= 0.01;

  // Per-line lifetime purchase limits (Sprint D). Pulls effective limit + how
  // many the user has already bought lifetime. Only fetched when logged in.
  const [limits, setLimits] = useState({}); // { [productId]: { remaining, max, purchased } }
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !cart.length) { setLimits({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/purchase-limits/bulk-effective', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ product_ids: cart.map(c => c.id) }),
        });
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        // Flatten the {product_limit, category_limits, most_restrictive} shape
        // down to {max, purchased, remaining} for the cart UI.
        const flat = {};
        for (const [pid, eff] of Object.entries(j || {})) {
          if (!eff) continue;
          const r = eff.most_restrictive;
          if (r) {
            flat[pid] = { max: r.max, purchased: (r.max ?? 0) - (r.remaining ?? 0), remaining: r.remaining };
          } else if (eff.product_limit?.max) {
            flat[pid] = { max: eff.product_limit.max, purchased: eff.product_limit.consumed, remaining: eff.product_limit.remaining };
          }
        }
        setLimits(flat);
      } catch (_) { /* limits feature is optional — don't break cart */ }
    })();
    return () => { cancelled = true; };
  }, [cart.map(c => c.id).join(',')]);

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const [quotesEnabled, setQuotesEnabled] = useState(false);
  useEffect(() => {
    fetch('/api/feature-flags/public')
      .then(r => r.ok ? r.json() : {})
      .then(flags => setQuotesEnabled(!!(flags && flags.quotes_rfq_enabled)))
      .catch(() => {});
  }, []);

  const [requestingQuote, setRequestingQuote] = useState(false);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const submitQuote = async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login?next=/cart'); return; }
    setRequestingQuote(true);
    try {
      const items = cart.map(c => ({
        product_id: c.id,
        quantity: c.quantity,
        unit_price_cents: Math.round(priceFor(c) * 100),
      }));
      const r = await fetch('/api/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ items, notes: quoteNotes }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      navigate('/quotes');
    } catch (e) {
      alert(e.message);
    } finally {
      setRequestingQuote(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="cart-empty">
        <h2>Your cart is empty</h2>
        <p>Browse our catalog to add Pokemon cards to your cart.</p>
        <Link to="/shop" className="btn btn-primary">
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="cart">
      <SEO title="Your cart" description="Review your Pokemon TCG order before checkout." noindex />
      <h1>Shopping Cart</h1>

      <div className="cart-content">
        <div className="cart-items">
          {cart.map(item => (
            <div key={item.id} className="cart-item">
              <div className="cart-item-image">
                <ProductImage product={item} alt={item.name} />
              </div>

              <div className="cart-item-details">
                <h3>{item.name}</h3>
                {item.set_name && (
                  <p className="item-set">
                    {item.set_name}
                    {item.card_number && ` #${item.card_number}`}
                  </p>
                )}
                {item.rarity && (
                  <span className={`rarity-badge rarity-${item.rarity.toLowerCase().replace(/\s+/g, '-')}`}>
                    {item.rarity}
                  </span>
                )}
                {(() => {
                  let mods = item.modifiers;
                  if (typeof mods === 'string') { try { mods = JSON.parse(mods); } catch (_) { mods = null; } }
                  if (!mods || typeof mods !== 'object' || !Object.keys(mods).length) return null;
                  return (
                    <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', fontSize: 12, color: '#4b5563' }}>
                      {Object.entries(mods).map(([k, v]) => (
                        <li key={k}><strong>{k}:</strong> {v}</li>
                      ))}
                    </ul>
                  );
                })()}
              </div>

              <div className="cart-item-price">
                ${priceFor(item).toFixed(2)}
                {resolvedPrices[item.id]?.group_price_applied && resolvedPrices[item.id].final < resolvedPrices[item.id].base && (
                  <small style={{ display: 'block', color: 'var(--color-success, #16a085)', fontSize: 11 }}>
                    <s style={{ color: 'var(--text-light)' }}>${resolvedPrices[item.id].base.toFixed(2)}</s>
                    {' '}<strong>{resolvedPrices[item.id].group?.name || 'Wholesale'}</strong>
                  </small>
                )}
                {!resolvedPrices[item.id]?.group_price_applied && resolvedPrices[item.id]?.tier && resolvedPrices[item.id].final < resolvedPrices[item.id].base && (
                  <small style={{ display: 'block', color: 'var(--color-success, #16a085)', fontSize: 11 }}>
                    <s style={{ color: 'var(--text-light)' }}>${resolvedPrices[item.id].base.toFixed(2)}</s>
                    {' '}{resolvedPrices[item.id].tier.name} tier
                  </small>
                )}
              </div>

              <div className="cart-item-quantity">
                <button
                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                  className="qty-btn"
                >
                  -
                </button>
                <input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                  min="1"
                  max={item.stock}
                />
                <button
                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                  className="qty-btn"
                  disabled={item.quantity >= item.stock || (limits[item.id] && item.quantity >= limits[item.id].remaining + (limits[item.id].in_cart || 0))}
                >
                  +
                </button>
              </div>

              <div className="cart-item-total">
                ${(priceFor(item) * item.quantity).toFixed(2)}
                {limits[item.id]?.max && (
                  <small style={{ display: 'block', fontSize: 11, color: 'var(--text-light)', marginTop: 2 }}>
                    Limit {limits[item.id].max} lifetime · {limits[item.id].purchased || 0} purchased · {Math.max(0, limits[item.id].remaining || 0)} left
                  </small>
                )}
              </div>

              <button
                onClick={() => removeFromCart(item.id)}
                className="remove-btn"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="cart-summary">
          <h2>Order Summary</h2>

          <div className="summary-row">
            <span>Subtotal:</span>
            <span>${getCartTotal().toFixed(2)}</span>
          </div>

          {showSavings && (
            <div className="summary-row" style={{ color: 'var(--color-success-dark, #137333)', fontWeight: 600 }}>
              <span>You're saving vs market:</span>
              <span>${marketSavings.toFixed(2)}</span>
            </div>
          )}

          <div className="summary-row">
            <span>Shipping:</span>
            <span>Calculated at checkout</span>
          </div>

          <div className="summary-total">
            <span>Total:</span>
            <span>${getCartTotal().toFixed(2)}</span>
          </div>

          <button onClick={handleCheckout} className="btn btn-primary btn-block">
            Proceed to Checkout
          </button>

          {quotesEnabled && (
            <>
              {!showQuoteForm ? (
                <button onClick={() => setShowQuoteForm(true)} className="btn btn-secondary btn-block" style={{ marginTop: 8 }}>
                  Request a Quote
                </button>
              ) : (
                <div style={{ marginTop: 12, padding: 12, border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb' }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Add a note for our team (optional)</label>
                  <textarea value={quoteNotes} onChange={e => setQuoteNotes(e.target.value)}
                    placeholder="Delivery timing, custom terms, etc."
                    style={{ width: '100%', minHeight: 60, marginTop: 4 }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={submitQuote} disabled={requestingQuote} className="btn btn-primary">
                      {requestingQuote ? 'Submitting…' : 'Submit Quote Request'}
                    </button>
                    <button onClick={() => setShowQuoteForm(false)} className="btn">Cancel</button>
                  </div>
                </div>
              )}
            </>
          )}

          <Link to="/shop" className="continue-shopping">
            Continue Shopping
          </Link>

          <button onClick={clearCart} className="btn btn-danger btn-block">
            Clear Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cart;
